import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@mui/material";
import React, { FC, useContext, useEffect, useRef, useState } from "react";
import { FiTrash } from "react-icons/fi";
import TextInputField from "../../../../components/editable/EditableField/fields/TextInputField";
import TimeField from "../../../../components/editable/EditableField/fields/TimeField";
import { ProgressView } from "../../../../components/notifications/progress/ProgressView";
import { api, ITimeSlot } from "../../../../logic/api";
import flex from "../../../../styles/flex.module.css";
import { SessionContext } from "../../../../utility/useSession";
import styles from "../../KmosPage.module.css";
import { useTranslation } from "react-i18next";
import { isDate, isValid, parseISO } from "date-fns";

export interface KmosTimeSlotEditorProps {
  show: boolean;
  readonly: boolean;
  onUpdate?: () => void;
  onClose: () => void;
}

export const KmosTimeSlotEditor: FC<KmosTimeSlotEditorProps> = ({
  show,
  readonly,
  onUpdate,
  onClose,
}) => {
  const session = useContext(SessionContext);
  const carParkId = session.current.site?.home.id;

  if (!carParkId) {
    return <></>;
  }

  const [timeSlots, setTimeSlots] = useState<ITimeSlot[] | null>();
  const [defaultTimeSlots, setDefaultTimeSlots] = useState<ITimeSlot[]>([]);
  const [editing, setEditing] = useState<boolean>(false);
  const [cancelEditWarn, setCancelEditWarn] = useState<boolean>(false);
  const { t } = useTranslation();

  const timeSlotApi = api.hooks.kmosV2.getTimeSlots();

  const cloneTimeSlot = (timeSlot: ITimeSlot) => ({ ...timeSlot });
  const cloneTimeSlots = (timeSlots: ITimeSlot[] | null) => timeSlots ? timeSlots.map(cloneTimeSlot) : [];

  const onEditClick = () => {
    setEditing(true);
  }

  const onCancelEditClick = () => {
    setCancelEditWarn(true);
  }

  const handleClose = (ok: boolean) => {
    setCancelEditWarn(false);

    if (ok) {
      setEditing(false);
      setTimeSlots(cloneTimeSlots(defaultTimeSlots));
    }
  }

  const onSave = () => {
    setEditing(false);
    if (onUpdate) {
      onUpdate();
    }
  }

  useEffect(() => {
    if (show && carParkId) {
      timeSlotApi.send(session.current.context, {}, (timeSlots: ITimeSlot[] | null) => {
        const newTimeSlots = cloneTimeSlots(timeSlots);
        if (newTimeSlots.length < 1) {
          newTimeSlots.push({
            active: true,
            endTime: "23:59",
            id: -1,
            letter: "N",
            segment: t("general.new"),
            startTime: "00:00",
          });
        }

        setTimeSlots(newTimeSlots);
        setDefaultTimeSlots(newTimeSlots);
      });
    }
  }, [carParkId, show]);

  return (
    <>
      <Dialog
        open={show}
        keepMounted
        maxWidth={"lg"}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle>{t("kmos.labels.kmosTimeSlots")}</DialogTitle>

        <ProgressView status={timeSlotApi.status}>
          {timeSlots && (
            editing
              ? <KmosTimeSlotEditorEditMode timeSlots={timeSlots} setTimeSlots={setTimeSlots} onCancelClick={onCancelEditClick} onSave={onSave} />
              : <KmosTimeSlotEditorViewMode timeSlots={timeSlots} onCancelClick={onClose} onEditClick={onEditClick} readonly={readonly} />
          )}
        </ProgressView>
      </Dialog>
      <KmosTimeSlotCancelEditAlert open={cancelEditWarn} handleClose={handleClose} />
    </>
  );
};

interface IKmosTimeSlotEditorProps {
  onCancelClick: () => void;
  onSave: () => void;
  timeSlots: ITimeSlot[];
  setTimeSlots: React.Dispatch<React.SetStateAction<ITimeSlot[] | null | undefined>>;
}

const KmosTimeSlotEditorEditMode: FC<IKmosTimeSlotEditorProps> = ({ onCancelClick, onSave, timeSlots, setTimeSlots }) => {
  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const [saving, setSaving] = useState<boolean>(false);
  const [newStateId, setNewStateId] = useState<number>(-1 - timeSlots.length);
  const [errors, setErrors] = useState<string[]>([]);

  const dialogContentRef = useRef<HTMLDivElement>(null);

  const syncSegments = api.hooks.kmosV2.syncSegments();

  const addTimeSlot = () => {
    const newTimeSlot: ITimeSlot = {
      active: true,
      endTime: "",
      id: newStateId,
      letter: "",
      segment: "",
      startTime: "",
    };

    setNewStateId(newStateId - 1);
    setTimeSlots((timeSlots) => [...(timeSlots || []), newTimeSlot]);
  };

  const deleteTimeSlot = (id: number) => {
    setTimeSlots((timeSlots) => {
      if (!timeSlots) {
        return timeSlots;
      }

      const newTimeSlots = [...(timeSlots || [])];
      const index = newTimeSlots.findIndex((s) => s.id === id);
      if (index === -1) {
        return timeSlots;
      }

      newTimeSlots.splice(index, 1);
      return newTimeSlots;
    })
  }

  const updateTimeSlot = (timeSlot: Partial<ITimeSlot>) => {
    setTimeSlots((timeSlots) => {
      if (!timeSlots) {
        return timeSlots;
      }

      const newTimeSlots = [...(timeSlots || [])];
      const index = newTimeSlots.findIndex((s) => s.id === timeSlot.id);
      if (index === -1) {
        return timeSlots;
      }

      const newTimeSlot = { ...(timeSlots[index] || {}), ...timeSlot };
      newTimeSlots.splice(index, 1, newTimeSlot);
      newTimeSlots.sort((a, b) => {
        if (!a.startTime || !b.startTime) return 0;
        if (a.startTime < b.startTime) return -1;
        if (b.startTime < a.startTime) return 1;
        return 0;
      });
      return newTimeSlots;
    });
  }

  const validateTimeSlot = (timeSlot: ITimeSlot) => {
    return timeSlot.letter && timeSlot.segment && timeSlot.startTime;
  }

  const validate = (timeSlots: ITimeSlot[]) => {
    const hasDuplicates = (a: string[]) => a.length !== new Set(a).size;
    const letters = timeSlots.map((timeSlot) => timeSlot.letter);
    const starts = timeSlots.map((timeSlot) => timeSlot.startTime);

    const errors: string[] = [];
    timeSlots.forEach((timeSlot, index) => !validateTimeSlot(timeSlot) && errors.push(t("errors.timeSlotIncomplete", { value: timeSlot.letter || timeSlot.segment || index })));
    hasDuplicates(letters) && errors.push(t("errors.timeSlotLettersUnique"));
    hasDuplicates(starts) && errors.push(t("errors.timeSlotStartTimeUnique"));
    setErrors(errors);
    return errors;
  }

  const save = () => {
    const errors = validate(timeSlots);
    if (errors?.length > 0) {
      if (dialogContentRef.current) {
        dialogContentRef.current.scrollTo(0, 0);
      }
      return;
    }

    setSaving(true);

    syncSegments.send(
      session.current.context,
      {
        segments: timeSlots,
      },
      (syncedTimeSlots) => {
        setSaving(false);
        setTimeSlots(syncedTimeSlots);
        onSave();
      }
    );
  };

  return <>
    <DialogContent ref={dialogContentRef}>
      <DialogContentText id="alert-dialog-slide-description">
        {t("kmos.labels.configTimeSlots")}
      </DialogContentText>

      <div className={styles.editDialogContent}>
        {errors.length > 0 && (
          <div className="MuiTypography-root MuiDialogContentText-root MuiTypography-body1 MuiTypography-colorTextSecondary">
            <ul className={styles.error}>
              {errors.map((error, index) => {
                return <li key={index}>{error}</li>
              })}
            </ul>
          </div>
        )}

        {timeSlots.map((timeSlot, index) => {
          return (
            <KmosTimeSlotEditorRow
              key={timeSlot.id.toString()}
              isMandatoryRow={index < 1}
              timeSlot={timeSlot}
              deleteTimeSlot={deleteTimeSlot}
              updateTimeSlot={updateTimeSlot}
            />
          );
        })}
      </div>
    </DialogContent>

    <DialogActions>
      <Button onClick={onCancelClick} color="secondary" disabled={saving}>
        {t("general.cancel")}
      </Button>
      <Button onClick={addTimeSlot} color="primary" disabled={saving}>
        {t("kmos.labels.newTimeSlot")}
      </Button>
      <Button onClick={save} color="primary" disabled={saving}>
        {t("general.save")}
      </Button>
    </DialogActions>
  </>
}

export interface KmosTimeSlotEditorRowProps {
  isMandatoryRow: boolean,
  deleteTimeSlot: (key: number) => void;
  timeSlot: ITimeSlot;
  updateTimeSlot: (timeSlot: Partial<ITimeSlot>) => void;
}

const KmosTimeSlotEditorRow: FC<KmosTimeSlotEditorRowProps> = ({
  isMandatoryRow,
  deleteTimeSlot,
  timeSlot,
  updateTimeSlot,
}) => {
  const { t } = useTranslation();
  const pluckTimeStringFromISO = (isoString: string) => {
    if (isDate(parseISO(isoString)) && isValid(parseISO(isoString))) {
      return isoString.substring(11, 16);
    }
  }

  return (
    <div className={flex.row}>
      <div className={flex.auto}>
        <div className={styles.field}>
          <TextInputField
            maxWidth={50}
            label={t("kmos.labels.letter")}
            value={timeSlot.letter}
            onChange={(letter) => updateTimeSlot({ id: timeSlot.id, letter })}
          ></TextInputField>
        </div>
      </div>
      <div className={flex.auto}>
        <div className={styles.field}>
          <TextInputField
            label={t("kmos.labels.timeSlot")}
            value={timeSlot.segment}
            onChange={(name) => updateTimeSlot({ id: timeSlot.id, segment: name })}
          ></TextInputField>
        </div>
      </div>
      <div className={flex.auto}>
        <div className={styles.field}>
          <TimeField
            fiveMinutes={false}
            label={t("kmos.labels.startTime")}
            value={`2000-01-01T${timeSlot.startTime}:00Z`}
            memoise
            localise={false}
            readOnly={isMandatoryRow}
            onChange={(value) => {
              const startTime = pluckTimeStringFromISO(value);

              updateTimeSlot({ id: timeSlot.id, startTime });
            }}
          ></TimeField>
        </div>
      </div>
      <div className={flex.auto}>
        <div className={styles.fieldActions}>
          {!isMandatoryRow && (
            <IconButton onClick={() => deleteTimeSlot(timeSlot.id)}>
              <FiTrash />
            </IconButton>
          )}
        </div>
      </div>
    </div>
  );
};

interface IKmosTimeSlotViewerProps {
  onCancelClick: () => void;
  onEditClick: () => void;
  timeSlots: ITimeSlot[];
  readonly: boolean;
}

const KmosTimeSlotEditorViewMode: FC<IKmosTimeSlotViewerProps> = ({ onCancelClick, onEditClick, timeSlots, readonly }) => {
  const { t } = useTranslation();

  return <>
    <DialogContent>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t("kmos.labels.letter")}</TableCell>
            <TableCell>{t("kmos.labels.timeSlot")}</TableCell>
            <TableCell>{t("kmos.labels.startTime")}</TableCell>
            <TableCell>{t("kmos.labels.endTime")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {timeSlots.map((timeSlot) => {
            return (
              <TableRow key={timeSlot.id.toString()}>
                <TableCell>
                  {timeSlot.letter}
                </TableCell>
                <TableCell>
                  {timeSlot.segment}
                </TableCell>
                <TableCell>
                  {timeSlot.startTime}
                </TableCell>
                <TableCell>
                  {timeSlot.endTime}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </DialogContent>

    <DialogActions>
      <Button onClick={onCancelClick} color="secondary">
        {t(readonly ? "general.close" : "general.cancel")}
      </Button>

      {!readonly && (<Button onClick={onEditClick} color="primary">
        {t("general.edit")}
      </Button>)}
    </DialogActions>
  </>
}

interface KmosTimeSlotCancelEditAlertProps {
  open: boolean;
  handleClose: (ok: boolean) => void;
}

const KmosTimeSlotCancelEditAlert: FC<KmosTimeSlotCancelEditAlertProps> = ({ open, handleClose }) => {
  const { t } = useTranslation();
  return <Dialog open={open}>
    <DialogTitle>
      {t("dialog.changesNotSaved.title")}
    </DialogTitle>
    <DialogContent>
      <DialogContentText id="alert-dialog-description">
        {t("dialog.changesNotSaved.message")}
      </DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button onClick={() => handleClose(false)} autoFocus>{t("general.no")}</Button>
      <Button onClick={() => handleClose(true)}>{t("general.yes")}</Button>
    </DialogActions>
  </Dialog>
}
